import { ColDef } from 'ag-grid-enterprise'

import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { ManagementReport } from '@fortem-monorepo/web-sdk'

import { useManagementReportQuery } from '@/pages/funds/management-report/query'
import { ManagementReportWidget } from '@/pages/funds/management-report/types'

export interface ReturnsGridProps extends ManagementReportWidget {}

export const ReturnsGrid: React.FC<ReturnsGridProps> = ({ params }) => {
  const query = useManagementReportQuery(params)

  return (
    <Widget>
      <WidgetHeaderWithQuery query={query} title="Returns" />
      <WidgetContent loading={query.isLoading}>
        <CustomAgGrid
          query={query}
          rowData={query.data?.returns}
          columnDefs={makeColDefs(params?.fund_name!)}
          statusBar={{ statusPanels: [] }}
        />
      </WidgetContent>
    </Widget>
  )
}

const formatter = numberFormatter({
  suffix: '%',
  transformer(val) {
    return val * 100
  },
})

const makeColDefs = (fund_name: string) => {
  type Data = Required<ManagementReport.GetManagementReportData>['returns'][number]
  type Field = keyof Data

  const map: Record<string, Field[]> = {
    FCPGF: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      '5_year',
      'since_inception',
    ],
    FCAGF: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      '5_year',
      'since_inception',
    ],
    FCDGF: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      // "5_year",
      'since_inception',
    ],
    FCARF: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      // "5_year",
      'since_inception',
    ],
    FCCAR: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      '5_year',
      'since_inception',
    ],
    FCUSI: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      // "5_year",
      'since_inception',
    ],
    FCLA1: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      // "1_year",
      // "3_year",
      // "5_year",
      'since_inception',
    ],
    LOWES: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      '5_year',
      'since_inception',
    ],
    FCGMS: [
      '1_month',
      '3_month',
      '6_month',
      'calendar_ytd',
      '1_year',
      '3_year',
      '5_year',
      'since_inception',
    ],
    FCMFF: ['1_month', 'calendar_ytd', 'since_inception'],
  }

  const defMap: Record<Field, ColDef<Data>> = {
    index: {},
    source: {},
    '1_month': {
      field: '1_month',
      headerName: '1m',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    '3_month': {
      field: '3_month',
      headerName: '3m',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    '6_month': {
      field: '6_month',
      headerName: '6m',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    calendar_ytd: {
      field: 'calendar_ytd',
      headerName: 'Year to Date',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    '1_year': {
      field: '1_year',
      headerName: '1y',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    '3_year': {
      field: '3_year',
      headerName: '3y',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    '5_year': {
      field: '5_year',
      headerName: '5y',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
    since_inception: {
      field: 'since_inception',
      headerName: 'Inception',
      valueFormatter: formatter,
      cellClass: 'text-center',
    },
  }

  const coldefs: ColDef<Data>[] = [
    {
      field: 'index',
      headerName: 'Index',
    },
  ]

  const fields = map[fund_name]

  for (const field of fields) {
    coldefs.push(defMap[field])
  }

  return coldefs
}
